import { useEffect, useState } from 'react';

/**
 * Reactive CSS media query hook. Returns whether the given media query currently matches.
 *
 * Returns `false` during SSR and on the first client render. After mount it subscribes to
 * `change` events on the underlying `MediaQueryList`, so the value updates if the user
 * resizes the window or rotates a device.
 *
 * @param query A valid CSS media query string (e.g. `'(max-width: 767px)'`).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);

    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => {
      mql.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}
